import type { ReactNode } from 'react';
import { Box, FormHelperText, IconButton, Link } from '@mui/material';
import type { LinkProps } from '@mui/material';
import { CircleHelp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { enumValueHelpRef, openHelpSection } from './helpRefs';

/**
 * "Read more" next to a message the DSL's `helpRef` explains: a refusal in the validation banner, panel or
 * outcome snackbar, an enum value in a form. Opens the help panel at the section. The click stops here, so
 * a row or an alert it sits in does not act on it too.
 */
export function HelpReadMoreLink({ sectionId, color = 'inherit' }: { sectionId: string; color?: LinkProps['color'] }) {
  const { t } = useTranslation();
  return (
    <Link
      component="button"
      type="button"
      color={color}
      underline="always"
      onClick={(event) => {
        event.stopPropagation();
        openHelpSection(sectionId);
      }}
      sx={{ verticalAlign: 'baseline', font: 'inherit' }}
    >
      {t('help.readMore')}
    </Link>
  );
}

/** "Read more" on a line of its own under a message, or nothing when the message names no section. */
export function HelpReadMoreLine({ sectionId, color }: { sectionId?: string; color?: LinkProps['color'] }) {
  if (!sectionId) return null;
  return (
    <Box sx={{ fontSize: '0.875rem', mt: 0.5 }}>
      <HelpReadMoreLink sectionId={sectionId} color={color} />
    </Box>
  );
}

/** The same, as a small icon, where a line of text does not fit: a grid cell. */
export function HelpReadMoreIcon({ sectionId }: { sectionId: string }) {
  const { t } = useTranslation();
  return (
    <IconButton
      size="small"
      aria-label={t('help.readMore')}
      title={t('help.readMore')}
      onClick={(event) => {
        event.stopPropagation();
        openHelpSection(sectionId);
      }}
      sx={{ p: 0.25 }}
    >
      <CircleHelp size={14} />
    </IconButton>
  );
}

/**
 * A grid cell of an enum value: the label as the column shows it, then "Read more" when the value names a
 * help section (a pending reason, say) for the grid's `entity`. Generated for enum columns whose enum has a
 * value with a `helpRef`.
 */
export function EnumValueHelpCell({ entity, enumType, value, label }: { entity: string; enumType: string; value: unknown; label: ReactNode }) {
  const sectionId = enumValueHelpRef(enumType, value, entity);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
      {sectionId && <HelpReadMoreIcon sectionId={sectionId} />}
    </Box>
  );
}

/**
 * "Read more" under a form's enum field whose value names a help section (a pending reason, say) for the
 * form's `entity`, as the field's helper text; nothing for any other value. The value's label in the field
 * stays as it is.
 */
export function EnumValueHelpText({ entity, enumType, value }: { entity?: string; enumType?: string; value: unknown }) {
  const sectionId = enumValueHelpRef(enumType, value, entity);
  if (!sectionId) return null;
  return (
    <FormHelperText>
      <HelpReadMoreLink sectionId={sectionId} color="primary" />
    </FormHelperText>
  );
}
